import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiPackage, FiClock, FiCheck } from 'react-icons/fi';
import axiosInstance from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import { getLocalizedField } from '../../utils/localize';
import type { SupportedLanguage } from '../../i18n';
import { getStatusLabel } from '../../utils/orderStatus';
import { getErrorDetail } from '../../utils/errorDetail';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BYOOrderItem {
  id: number;
  order_id: number;
  option_item_id: number;
  option_name: string;
  option_name_zh: string | null;
  unit_price: number;
}

interface BYOOrder {
  id: number;
  order_id: number | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  pickup_date: string | null;
  pickup_time: string | null;
  total_price: number;
  base_price: number;
  special_instructions: string | null;
  status: string;
  created_at: string | null;
  items: BYOOrderItem[];
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const fetchBYOOrders = async (): Promise<BYOOrder[]> => {
  const response = await axiosInstance.get('/admin/byo-options/byo-orders');
  return response.data;
};

const updateBYOOrderStatus = async ({ id, status }: { id: number; status: string }) => {
  const response = await axiosInstance.patch(`/admin/byo-options/byo-orders/${id}/status`, { status });
  return response.data;
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const Table = styled.table`
  width: 100%;
  background: #F5F0E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #1C1511;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #E8DDD0;

  &:hover {
    background: #ede8df;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1C1511;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'pending': return '#fef3c7';
      case 'confirmed': return '#e0e7ff';
      case 'preparing': return '#e0e7ff';
      case 'ready': return '#d1fae5';
      case 'completed': return '#d1fae5';
      case 'cancelled': return '#fee2e2';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'pending': return '#92400e';
      case 'confirmed': return '#3730a3';
      case 'preparing': return '#3730a3';
      case 'ready': return '#065f46';
      case 'completed': return '#065f46';
      case 'cancelled': return '#991b1b';
      default: return '#6b7280';
    }
  }};
`;

const StatusSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 13px;
  margin-right: 8px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const UpdateButton = styled.button`
  padding: 6px 12px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
  background: #F5F0E8;
  border-radius: 12px;
`;

const ItemsSummary = styled.div`
  font-size: 13px;
  color: #6B5E4E;
  max-width: 240px;
`;

const LinkedOrderNote = styled.div`
  font-size: 12px;
  color: #6B5E4E;
  margin-top: 4px;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;


// ─── Row component ────────────────────────────────────────────────────────────

interface OrderRowProps {
  order: BYOOrder;
  onUpdateStatus: (id: number, status: string) => void;
  isUpdating: boolean;
}

const OrderRow: React.FC<OrderRowProps> = ({ order, onUpdateStatus, isUpdating }) => {
  const { t, i18n } = useTranslation();
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [selectedStatus, setSelectedStatus] = useState(order.status);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <FiClock size={12} />;
      case 'confirmed':
      case 'preparing': return <FiPackage size={12} />;
      case 'ready':
      case 'completed': return <FiCheck size={12} />;
      default: return null;
    }
  };

  const formatPickup = () => {
    if (!order.pickup_date) return '—';
    return `${order.pickup_date}${order.pickup_time ? ' ' + order.pickup_time : ''}`;
  };

  return (
    <Tr>
      <Td>
        #{order.id}
        {order.order_id !== null && (
          <LinkedOrderNote>{t('admin.byoOrders.linkedOrderLabel', { id: order.order_id })}</LinkedOrderNote>
        )}
      </Td>
      <Td>{order.customer_name}</Td>
      <Td>{order.customer_phone}</Td>
      <Td>{order.customer_email}</Td>
      <Td>{formatPickup()}</Td>
      <Td>
        <StatusBadge status={order.status}>
          {getStatusIcon(order.status)}
          {getStatusLabel(order.status, t)}
        </StatusBadge>
      </Td>
      <Td>{formatCurrency(order.total_price)}</Td>
      <Td>
        <ItemsSummary>
          {Array.isArray(order.items) && order.items.length > 0
            ? order.items.map(item => (
                <div key={item.id}>
                  {getLocalizedField(item, 'option_name', language) as string}
                  {item.unit_price > 0 && ` (+${formatCurrency(item.unit_price)})`}
                </div>
              ))
            : <span style={{ color: '#9ca3af' }}>{t('admin.byoOrders.noItemsLabel')}</span>
          }
        </ItemsSummary>
      </Td>
      <Td>{formatDate(order.created_at)}</Td>
      <Td>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StatusSelect
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
          >
            <option value="pending">{t('admin.byoOrders.statusOptions.pending')}</option>
            <option value="confirmed">{t('admin.byoOrders.statusOptions.confirmed')}</option>
            <option value="preparing">{t('admin.byoOrders.statusOptions.preparing')}</option>
            <option value="ready">{t('admin.byoOrders.statusOptions.ready')}</option>
            <option value="completed">{t('admin.byoOrders.statusOptions.completed')}</option>
            <option value="cancelled">{t('admin.byoOrders.statusOptions.cancelled')}</option>
          </StatusSelect>
          <UpdateButton
            onClick={() => onUpdateStatus(order.id, selectedStatus)}
            disabled={selectedStatus === order.status || isUpdating}
          >
            {t('admin.byoOrders.updateButton')}
          </UpdateButton>
        </div>
      </Td>
    </Tr>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminBYOOrders: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [statusError, setStatusError] = useState<string | null>(null);

  const { data: rawOrders, isLoading } = useQuery({
    queryKey: ['adminBYOOrders'],
    queryFn: fetchBYOOrders,
  });
  const orders = Array.isArray(rawOrders) ? rawOrders : [];

  const updateStatusMutation = useMutation({
    mutationFn: updateBYOOrderStatus,
    onSuccess: () => {
      setStatusError(null);
      queryClient.invalidateQueries({ queryKey: ['adminBYOOrders'] });
    },
    onError: (err: any) => {
      setStatusError(getErrorDetail(err) || t('admin.byoOrders.updateStatusFailedError'));
    },
  });

  const handleUpdateStatus = (id: number, status: string) => {
    updateStatusMutation.mutate({ id, status });
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.byoOrders.title')}</Title>
      </Header>

      {statusError && <ErrorMessage>{statusError}</ErrorMessage>}

      {orders.length === 0 ? (
        <EmptyState>
          <FiPackage size={48} />
          <h3>{t('admin.byoOrders.emptyTitle')}</h3>
          <p>{t('admin.byoOrders.emptyDescription')}</p>
        </EmptyState>
      ) : (
        <Table data-testid="byo-orders-table">
          <Thead>
            <tr>
              <Th>{t('admin.byoOrders.orderIdHeader')}</Th>
              <Th>{t('admin.byoOrders.customerNameHeader')}</Th>
              <Th>{t('admin.byoOrders.phoneHeader')}</Th>
              <Th>{t('admin.byoOrders.emailHeader')}</Th>
              <Th>{t('admin.byoOrders.pickupHeader')}</Th>
              <Th>{t('admin.byoOrders.statusHeader')}</Th>
              <Th>{t('admin.byoOrders.totalHeader')}</Th>
              <Th>{t('admin.byoOrders.itemsHeader')}</Th>
              <Th>{t('admin.byoOrders.createdAtHeader')}</Th>
              <Th>{t('admin.byoOrders.actionsHeader')}</Th>
            </tr>
          </Thead>
          <tbody>
            {orders.map(order => (
              <OrderRow
                key={order.id}
                order={order}
                onUpdateStatus={handleUpdateStatus}
                isUpdating={updateStatusMutation.isPending}
              />
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AdminBYOOrders;
